import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader, Button, Badge } from "@/components/ui/PageHeader";
import {
  getInstitution,
  addInstitutionDocument,
  submitInstitutionVerification,
  type InstitutionDocumentType,
} from "@/api/institutions";
import { createEvent, listCategories } from "@/api/events";
import { HostPeoplePanel } from "@/components/HostPeoplePanel";
import { uploadDocument, ALLOWED_DOCUMENT_TYPES } from "@/api/upload";
import { listOpportunitiesForInstitution, listSignups, acceptSignup, rejectSignup, completeSignup, type OpportunitySignup } from "@/api/volunteers";
import { VisitorRulesSection } from "./VisitorRulesSection";

/**
 * Institution management — the three moments the Lab never covered:
 * verification document upload (submit 400s without ≥1 doc), worship-event
 * creation, and volunteer signup review (accept → complete pays the
 * headline Yash award). All gates are server-enforced (institution
 * membership); the Lab surfaces the errors.
 */
export default function InstitutionManageLab() {
  const { id } = useParams<{ id: string }>();
  const { data } = useQuery({
    queryKey: ["institution", id],
    queryFn: () => getInstitution(id!),
    enabled: !!id,
  });
  const inst = data?.data;

  if (!id) return null;

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Manage: ${inst?.name ?? "Institution"}`}
        subtitle="Verification documents, worship events, and volunteer review"
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Institutions", href: "/lab/institutions" },
          { label: "Manage" },
        ]}
      />
      <DocumentsSection institutionId={id} />
      <VisitorRulesSection institutionId={id} />
      <WorshipEventSection institutionId={id} city={inst?.city} state={inst?.state} />
      <VolunteerReviewSection institutionId={id} />
      <HostPeoplePanel kind="institution" id={id} />
    </div>
  );
}

const DOC_TYPES: { value: InstitutionDocumentType; label: string }[] = [
  { value: "irs_501c3_letter", label: "IRS 501(c)(3) letter" },
  { value: "ein_confirmation", label: "EIN confirmation" },
  { value: "state_registration", label: "State registration" },
  { value: "articles_of_incorporation", label: "Articles of incorporation" },
  { value: "board_resolution", label: "Board resolution" },
  { value: "utility_bill", label: "Utility bill" },
  { value: "photo_id_contact", label: "Contact photo ID" },
  { value: "institution_exterior", label: "Building exterior photo" },
  { value: "institution_interior", label: "Building interior photo" },
  { value: "other", label: "Other" },
];

function DocumentsSection({ institutionId }: { institutionId: string }) {
  const [docType, setDocType] = useState<InstitutionDocumentType>("irs_501c3_letter");
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Two steps: the file goes to POST /upload/document (private), then its
  // address is attached here. The attach accepts only your own document.
  async function upload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    setMsg(null);
    try {
      const doc = await uploadDocument(file);
      await addInstitutionDocument(institutionId, {
        documentType: docType,
        fileUrl: doc.url,
        fileName: doc.fileName,
      });
      setMsg(`${doc.fileName} attached ✓`);
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  async function submitVerification() {
    setBusy(true);
    setMsg(null);
    try {
      await submitInstitutionVerification(institutionId);
      setMsg("Submitted for verification ✓");
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="bg-white border border-gray-200 rounded-2xl p-5 space-y-3">
      <h2 className="text-lg font-semibold">Verification documents</h2>
      <p className="text-xs text-gray-500">
        Verification submission requires at least one attached document.
      </p>
      <div className="flex gap-2 flex-wrap">
        <select className="input w-56" value={docType} onChange={(e) => setDocType(e.target.value as InstitutionDocumentType)}>
          {DOC_TYPES.map((d) => (
            <option key={d.value} value={d.value}>{d.label}</option>
          ))}
        </select>
        <input
          type="file"
          className="input flex-1 min-w-64"
          accept={ALLOWED_DOCUMENT_TYPES.join(",")}
          disabled={busy}
          onChange={upload}
        />
      </div>
      <Button size="sm" variant="secondary" disabled={busy} onClick={submitVerification}>
        Submit for verification
      </Button>
      {msg && <p className="text-sm text-gray-600">{msg}</p>}
    </section>
  );
}

function WorshipEventSection({ institutionId, city, state }: { institutionId: string; city?: string; state?: string }) {
  const { data: cats } = useQuery({ queryKey: ["event-categories"], queryFn: listCategories });
  const categories = cats?.data?.categories ?? [];
  const [title, setTitle] = useState("");
  const [categoryCode, setCategoryCode] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [startTime, setStartTime] = useState("18:00");
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function create() {
    setBusy(true);
    setMsg(null);
    try {
      const res = await createEvent({
        title,
        categoryCode,
        eventDate,
        startTime,
        city: city ?? "",
        state: state ?? "",
        realm: "worship",
        organizerInstitutionId: institutionId,
      });
      setMsg(`Created ✓ (${res.data?.id ?? "event"}) — followers are notified`);
      setTitle("");
      setEventDate("");
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="bg-white border border-gray-200 rounded-2xl p-5 space-y-3">
      <h2 className="text-lg font-semibold">Create worship event</h2>
      <p className="text-xs text-gray-500">
        Realm=worship, organized by this institution — fans out to followers and awards the creation points.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input className="input sm:col-span-2" placeholder="Title (min 5 chars) — e.g. Diwali Lakshmi Puja" value={title} onChange={(e) => setTitle(e.target.value)} />
        <select className="input" value={categoryCode} onChange={(e) => setCategoryCode(e.target.value)}>
          <option value="">Category…</option>
          {categories.map((c) => (
            <option key={c.code} value={c.code}>{c.name}</option>
          ))}
        </select>
        <input className="input" type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)} />
        <input className="input" type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
      </div>
      <Button size="sm" disabled={busy || title.length < 5 || !categoryCode || !eventDate} onClick={create}>
        Create worship event
      </Button>
      {msg && <p className="text-sm text-gray-600">{msg}</p>}
    </section>
  );
}

function VolunteerReviewSection({ institutionId }: { institutionId: string }) {
  const { data } = useQuery({
    queryKey: ["inst-opportunities", institutionId],
    queryFn: () => listOpportunitiesForInstitution(institutionId),
  });
  const opportunities = data?.data?.items ?? [];

  return (
    <section className="bg-white border border-gray-200 rounded-2xl p-5 space-y-4">
      <h2 className="text-lg font-semibold">Volunteer review</h2>
      {opportunities.length === 0 && (
        <p className="text-sm text-gray-500">
          No opportunities yet — create one from the <Link className="text-primary-600 hover:underline" to="/lab/volunteers">volunteers page</Link>.
        </p>
      )}
      {opportunities.map((o) => (
        <OpportunitySignups key={o.id} opportunityId={o.id} title={o.title} />
      ))}
    </section>
  );
}

function OpportunitySignups({ opportunityId, title }: { opportunityId: string; title: string }) {
  const qc = useQueryClient();
  const [msg, setMsg] = useState<string | null>(null);
  const { data } = useQuery({
    queryKey: ["opp-signups", opportunityId],
    queryFn: () => listSignups(opportunityId),
  });
  const signups = data?.data?.signups ?? [];

  async function act(fn: () => Promise<unknown>, label: string) {
    setMsg(null);
    try {
      await fn();
      setMsg(`${label} ✓`);
      qc.invalidateQueries({ queryKey: ["opp-signups", opportunityId] });
    } catch (e) {
      setMsg(e instanceof Error ? e.message : `${label} failed`);
    }
  }

  return (
    <div className="border border-gray-200 rounded-xl p-4">
      <h3 className="font-medium mb-2">{title}</h3>
      {signups.length === 0 && <p className="text-sm text-gray-500">No signups yet.</p>}
      <ul className="space-y-2">
        {signups.map((s: OpportunitySignup) => (
          <li key={s.id} className="flex items-center gap-3 flex-wrap text-sm">
            <span className="font-medium">{s.user.name}</span>
            <Badge variant={s.status === "completed" ? "green" : "gray"}>{s.status}</Badge>
            {s.message && <span className="text-gray-500">“{s.message}”</span>}
            <span className="flex gap-2 ml-auto">
              {s.status === "applied" && (
                <>
                  <Button size="sm" onClick={() => act(() => acceptSignup(opportunityId, s.id), "Accepted")}>
                    Accept
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => act(() => rejectSignup(opportunityId, s.id, "Not a fit this time"), "Rejected")}>
                    Reject
                  </Button>
                </>
              )}
              {s.status === "accepted" && (
                <Button size="sm" onClick={() => act(() => completeSignup(opportunityId, s.id), "Completed — Yash awarded")}>
                  Mark completed
                </Button>
              )}
            </span>
          </li>
        ))}
      </ul>
      {msg && <p className="text-sm text-gray-600 mt-2">{msg}</p>}
    </div>
  );
}
